import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtCVE0 } from "@/lib/format";
import { periodLabel, saving } from "@/lib/pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CircleNotch, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";

const NAMES = { essencial: "Essencial", pro: "Pro", empresa: "Empresa" };
const num = (v) => (v === "" ? null : Number(v));

function FeaturesField({ k, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Funcionalidades (uma por linha)</Label>
      <Textarea rows={4} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} data-testid={`features-${k}`} />
    </div>
  );
}

function PlanEditor({ k, plan, durations, founderPct, onChange }) {
  const upd = (patch) => onChange({ ...plan, ...patch });
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5" data-testid={`plan-editor-${k}`}>
      <h2 className="font-display text-lg font-bold">{NAMES[k]}</h2>
      <div className="space-y-2">
        {durations.map((d) => {
          const v = plan.prices?.[String(d)];
          const off = saving(plan.prices, d);
          return (
            <div key={d} className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="text-xs">{periodLabel(d)}</Label>
              <div>
                <Input type="number" min="0" value={v ?? ""} data-testid={`price-${k}-${d}`}
                  onChange={(e) => upd({ prices: { ...plan.prices, [String(d)]: num(e.target.value) } })} />
                <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`price-hint-${k}-${d}`}>
                  {off > 0 ? `−${off}% vs mensal · ` : ""}Fundador: {v ? fmtCVE0(Math.round(v * (1 - founderPct / 100))) : "—"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <MaxUsers k={k} value={plan.max_users} onChange={(v) => upd({ max_users: v })} />
      <FeaturesField k={k} value={plan.features} onChange={(features) => upd({ features })} />
    </section>
  );
}

function MaxUsers({ k, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Máx. utilizadores (vazio = ilimitado)</Label>
      <Input type="number" min="1" value={value ?? ""} onChange={(e) => onChange(num(e.target.value))} data-testid={`max-users-${k}`} />
    </div>
  );
}

function FounderEditor({ founder, used, onChange }) {
  const upd = (patch) => onChange({ ...founder, ...patch });
  return (
    <section className="space-y-4 rounded-xl border border-amber-300 bg-amber-50/60 p-5" data-testid="founder-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Crown size={20} weight="fill" className="text-warn" /><h2 className="font-display text-lg font-bold">Preço Fundador</h2></div>
        <label className="flex items-center gap-2 text-sm">Ativo <Switch checked={founder.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="founder-enabled" /></label>
      </div>
      <p className="text-sm text-muted-foreground">Aplicado às primeiras empresas que ativam um plano pago, para sempre enquanto a subscrição se mantiver ativa. Quem deixa de pagar perde o desconto.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5"><Label className="text-xs">Desconto (%)</Label>
          <Input type="number" min="0" max="90" value={founder.discount_pct} onChange={(e) => upd({ discount_pct: Number(e.target.value) })} data-testid="founder-pct" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Nº máximo de empresas</Label>
          <Input type="number" min="0" value={founder.max_orgs} onChange={(e) => upd({ max_orgs: Number(e.target.value) })} data-testid="founder-max" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Lugares usados</Label>
          <div className="flex h-10 items-center font-display text-xl font-extrabold" data-testid="founder-used">{used ?? 0} / {founder.max_orgs}</div></div>
      </div>
    </section>
  );
}

function EmpresaEditor({ plan, contact, onPlan, onContact }) {
  const c = (k) => (e) => onContact({ ...contact, [k]: e.target.value });
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5" data-testid="plan-editor-empresa">
      <h2 className="font-display text-lg font-bold">Empresa <span className="text-sm font-normal text-muted-foreground">· bloco de contacto</span></h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label className="text-xs">WhatsApp</Label><Input value={contact.whatsapp} onChange={c("whatsapp")} placeholder="+238 ..." data-testid="contact-whatsapp" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={contact.email} onChange={c("email")} data-testid="contact-email" /></div>
      </div>
      <div className="space-y-1.5"><Label className="text-xs">Texto</Label><Textarea rows={2} value={contact.text} onChange={c("text")} data-testid="contact-text" /></div>
      <MaxUsers k="empresa" value={plan.max_users} onChange={(v) => onPlan({ ...plan, max_users: v })} />
      <FeaturesField k="empresa" value={plan.features} onChange={(features) => onPlan({ ...plan, features })} />
    </section>
  );
}

export default function PlatformPlans() {
  const [f, setF] = useState(null);
  const [used, setUsed] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/platform/pricing").then((r) => setF(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/platform/overview").then((r) => setUsed(r.data.founders?.used)).catch(() => {});
  }, []);
  if (!f) return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;

  const setPlan = (k) => (p) => setF({ ...f, plans: { ...f.plans, [k]: p } });
  const save = async () => {
    setBusy(true);
    try {
      const plans = Object.fromEntries(Object.entries(f.plans).map(([k, p]) => [k, { ...p, features: p.features.filter((x) => x.trim()) }]));
      const { data } = await api.put("/platform/pricing", { ...f, plans });
      setF(data);
      toast.success("Planos e preços guardados");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="platform-plans-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="rounded-full" asChild><Link to="/plataforma"><ArrowLeft size={18} /></Link></Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Super-admin</p>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Planos e preços</h1>
          </div>
        </div>
        <Button className="rounded-full" onClick={save} disabled={busy} data-testid="save-platform-pricing">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button>
      </div>

      <section className="grid gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-2">
        <div className="space-y-1.5"><Label className="text-xs">Período selecionado por defeito</Label>
          <Select value={String(f.default_months)} onValueChange={(v) => setF({ ...f, default_months: Number(v) })}>
            <SelectTrigger data-testid="default-months"><SelectValue /></SelectTrigger>
            <SelectContent>{f.durations.map((d) => <SelectItem key={d} value={String(d)}>{periodLabel(d)}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Câmbio para cartão (CVE por 1 EUR)</Label>
          <Input type="number" step="0.001" min="1" value={f.eur_rate} onChange={(e) => setF({ ...f, eur_rate: Number(e.target.value) })} data-testid="eur-rate" /></div>
      </section>

      <FounderEditor founder={f.founder} used={used} onChange={(founder) => setF({ ...f, founder })} />

      <div className="grid gap-4 lg:grid-cols-3">
        {["essencial", "pro"].map((k) => (
          <PlanEditor key={k} k={k} plan={f.plans[k]} durations={f.durations} founderPct={f.founder.enabled ? f.founder.discount_pct : 0} onChange={setPlan(k)} />
        ))}
        <EmpresaEditor plan={f.plans.empresa} contact={f.contact} onPlan={setPlan("empresa")} onContact={(contact) => setF({ ...f, contact })} />
      </div>
    </div>
  );
}
